'use client';

/**
 * BranchList — 지점 카드 목록 (슬라이더)
 *
 * 지점 카드들을 좌우 스크롤 캐러셀로 표시. 상단 제목 + 하단/우측 더보기 버튼 포함.
 * variant에 따라 상세형/컴팩트형 지점 카드로 자동 전환.
 *
 * 연관 파일:
 *  - CardRecommendBranch.tsx : 지점 카드 및 BranchDetailItem, BranchCompactItem 타입 정의
 *  - Carousel.tsx : 슬라이더 구현체
 *  - BranchList.module.scss : 레이아웃 스타일
 *
 * [Props]
 * - `variant`: "detail" (기본값) | "compact" — 카드 타입 선택
 * - `items`: BranchDetailItem[] | BranchCompactItem[] (필수)
 * - `title`: ReactNode — 섹션 제목 (선택)
 * - `button`: ButtonConfig — 더보기 버튼 액션. href 또는 leadForm action 등을 선언
 * - `cardTheme`: "primary" | "secondary" — variant="detail"일 때만 적용
 *
 * [BranchDetailItem 필드] — variant="detail"
 * - `name`: string (필수) — 지점명
 * - `href`: string (필수) — 카드 클릭 링크
 * - `images`: [pc, mobile?] (필수) — PC/모바일 썸네일 이미지
 * - `details`: { area; deposit; monthly } (필수) — 상세 정보
 * - `location`: string — 지점 위치 (선택)
 * - `tag`: BadgeVariant[] — 태그 배열 (선택)
 * - `id`: string — 고유 키 (선택, 없으면 href+index로 자동 생성)
 *
 * [BranchCompactItem 필드] — variant="compact"
 * - `name`, `href`, `images` 동일 (필수)
 * - `subTitle`: string — 부제목 (선택)
 * - `tag`: BadgeVariant[] — 태그 배열 (선택)
 *
 * [사용 예]
 * ```tsx
 * <BranchList
 *   title="추천 지점"
 *   items={branchDetailItems}
 *   button={{ text: '더 알아보기', href: '/branch' }}
 * />
 * ```
 */

import clsx from 'clsx';
import s from './BranchList.module.scss';
import CardRecommendBranch from '@publishing/modules/card/CardRecommendBranch';
import { Navigation } from 'swiper';
import { Carousel } from '@publishing/modules/carousel/Carousel';
import type { BranchCardTheme, BranchDetailItem, BranchCompactItem } from '@publishing/modules/card/CardRecommendBranch';
import type { ReactNode } from 'react';
import { Button, type ButtonConfig } from '@publishing/components/ui/Button';
export type { BranchDetailItem, BranchCompactItem, BranchCardTheme };

export type BranchListVariant = 'detail' | 'compact';

type BranchListCommonProps = {
  title?: ReactNode;
  button?: ButtonConfig;
};

type BranchListProps =
  | (BranchListCommonProps & {
      variant?: 'detail';
      items: BranchDetailItem[];
      cardTheme?: BranchCardTheme;
    })
  | (BranchListCommonProps & {
      variant: 'compact';
      items: BranchCompactItem[];
    });

export default function BranchList(props: BranchListProps) {
  const { title, button = { text: '더 알아보기', href: '#' } } = props;
  const { text: buttonText, ...btnProps } = button;
  const variant: BranchListVariant = props.variant ?? 'detail';

  const moreButton = (
    <Button size='medium' type='white' outline className={s.branch_more} {...btnProps}>
      {buttonText}
    </Button>
  );

  return (
    <div className={s.branch}>
      <div className={s.branch_header}>
        <h2 className={s.branch_title}>{title}</h2>
        <div className="hidden_mo">{moreButton}</div>
      </div>
      <Carousel
        modules={[Navigation]}
        className={clsx(s.branch_carousel_swiper, variant === 'compact' && s['branch_carousel_swiper--compact'])}
        navigationClassName={s.branch_carousel_wrapper}
      >
        {props.items.map((item, index) => (
          <CardRecommendBranch
            theme={props.variant === 'compact' ? undefined : props.cardTheme}
            key={item.id ?? `${item.href}-${index}`}
            {...item}
          />
        ))}
      </Carousel>
      <div className="hidden_pc">{moreButton}</div>
    </div>
  );
}
